import {useCallback, useMemo, useState} from 'react';
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react';
import {DocumentIcon, DocumentTextIcon, EllipsisVerticalIcon, ReceiptPercentIcon} from '@heroicons/react/24/outline';
import Popup from '../../../../../components/general/Popup.tsx';
import {CreditCardStatementIcon} from '../../../../../components/general/CustomIcons/CreditCardStatementIcon';
import {
    createTable,
    SortingState as TableSortingState,
    TableHeaderSpec,
} from '../../../../../components/table/CreateTable';
import {cleanDocumentFileName} from '../../../../../lib/TFICLib';
import {DOCUMENT_CATEGORY_LABELS} from '../../../../../types/enums.ts';
import {DocumentToCheck, DocumentsToCheckSorting} from './documentsToCheck.ts';
import {ToCheckReasonTag} from './ToCheckReasonTag.tsx';
import {BillTodoTag} from '../../../../../components/specific/BillTodoTag.tsx';
import type {DocumentCategory, DocumentsToCheckOrderByEnum} from '../../../../../generated/enums.ts';

const SORT_COLUMNS: Record<DocumentsToCheckOrderByEnum, true> = {
    created_at: true,
    file_name: true,
    business_name: true,
    document_category: true,
    reason: true,
    invoice_date: true,
};

function isSortColumn(field: string): field is DocumentsToCheckOrderByEnum {
    return Object.prototype.hasOwnProperty.call(SORT_COLUMNS, field);
}

type DocumentsToCheckTableProps = {
    documents: DocumentToCheck[];
    isLoading: boolean;
    sorting: DocumentsToCheckSorting;
    onSortingChange: (sorting: DocumentsToCheckSorting) => void;
    onRowClick?: (documentId: string, businessId: string) => void;
    onDelete?: (document: DocumentToCheck) => Promise<void>;
    onPossibleDuplicateClick?: (billId: string) => void;
};

function DocumentActionsMenu({document, onDeleteClick}: {
    document: DocumentToCheck;
    onDeleteClick: (document: DocumentToCheck) => void;
}) {
    return (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <Menu as="div" className="relative inline-block text-left">
                <MenuButton
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={(e) => e.stopPropagation()}
                >
                    <EllipsisVerticalIcon className="h-5 w-5 text-gray-600" aria-hidden="true"/>
                </MenuButton>
                <MenuItems
                    anchor={{to: 'bottom end', gap: 2}}
                    transition
                    className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                    <MenuItem as="div" className="relative flex items-center">
                        {() => (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteClick(document);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                                Verwijder
                            </button>
                        )}
                    </MenuItem>
                </MenuItems>
            </Menu>
        </div>
    );
}

function documentIcon(category: DocumentCategory | null) {
    switch (category) {
        case 'invoice':
            return DocumentTextIcon;
        case 'pos_receipt':
            return ReceiptPercentIcon;
        case 'credit_card_statement':
            return CreditCardStatementIcon;
        default:
            return DocumentIcon;
    }
}

function formatDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric'});
}

export function DocumentsToCheckTable({
    documents,
    isLoading,
    sorting,
    onSortingChange,
    onRowClick,
    onDelete,
    onPossibleDuplicateClick,
}: DocumentsToCheckTableProps) {
    const [documentPendingDeletion, setDocumentPendingDeletion] = useState<DocumentToCheck | null>(null);

    const handleDeleteClick = useCallback((document: DocumentToCheck) => {
        setDocumentPendingDeletion(document);
    }, []);

    const handleDeleteConfirm = async () => {
        if (!documentPendingDeletion || !onDelete) return;

        try {
            await onDelete(documentPendingDeletion);
        } finally {
            setDocumentPendingDeletion(null);
        }
    };

    const [currentSortingState, setCurrentSortingState] = useState<TableSortingState | null>({
        orderBy: sorting.orderby,
        orderDescending: sorting.order === 'desc',
    });

    const tableSpec = useMemo<TableHeaderSpec<DocumentToCheck>>(() => ({
        title: documents.length === 1 ? '1 document' : `${documents.length} documenten`,
        isLoading,
        options: {
            disableTopPaginationWidget: false,
            disableBottomPaginationWidget: true,
            allowFullRowClick: true,
        },
        onRowClick: (row) => {
            if (onRowClick) onRowClick(row.id, row.business_id);
        },
        sorting: {
            sortingState: [currentSortingState, setCurrentSortingState],
            inMemory: false,
        },
        onQueryUpdate: (_pagination, updatedSorting) => {
            onSortingChange({
                orderby: isSortColumn(updatedSorting.orderBy) ? updatedSorting.orderBy : 'created_at',
                order: updatedSorting.orderDescending ? 'desc' : 'asc',
            });
        },
        onEmptyRender: () => (
            <p className="text-gray-500 pt-12 text-center">Geen documenten om te controleren.</p>
        ),
        columns: [
            {
                name: 'Document',
                field: 'file_name',
                style: {widthMultiplier: 1.8, skeleton: 'lead'},
                customRender: (document: DocumentToCheck) => {
                    const IconComponent = documentIcon(document.document_category);
                    return (
                        <div className="flex items-center gap-4">
                            <IconComponent className="h-8 w-8 shrink-0 text-gray-400" aria-hidden="true" />
                            <span className="text-sm font-medium text-gray-900">
                                {cleanDocumentFileName(document.file_name)}
                            </span>
                        </div>
                    );
                },
            },
            {
                name: 'Uploaddatum',
                field: 'created_at',
                customRender: (document: DocumentToCheck) => (
                    <span className="text-sm text-gray-700">{formatDate(document.created_at)}</span>
                ),
            },
            {
                name: 'Factuurdatum',
                field: 'invoice_date',
                customRender: (document: DocumentToCheck) => (
                    <span className="text-sm text-gray-700">
                        {document.invoice_date ? formatDate(document.invoice_date) : ''}
                    </span>
                ),
            },
            {
                name: 'Categorie',
                field: 'document_category',
                customRender: (document: DocumentToCheck) => (
                    <span className={document.document_category ? 'text-sm text-gray-700' : 'text-sm text-gray-400'}>
                        {document.document_category
                            ? DOCUMENT_CATEGORY_LABELS[document.document_category]
                            : '—'}
                    </span>
                ),
            },
            {
                name: 'Status',
                field: 'reason',
                customRender: (document: DocumentToCheck) => (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {document.reason && <ToCheckReasonTag reason={document.reason} />}
                        {document.todos.map((todoType) => {
                            const billId = document.bill_id;
                            const opensDuplicates = todoType === 'possible_duplicate' && billId && onPossibleDuplicateClick;
                            return (
                                <BillTodoTag
                                    key={todoType}
                                    todoType={todoType}
                                    onClick={opensDuplicates ? () => onPossibleDuplicateClick(billId) : undefined}
                                />
                            );
                        })}
                    </div>
                ),
            },
            {
                name: 'Klant',
                field: 'business_name',
                customRender: (document: DocumentToCheck) => (
                    <span className="text-sm text-gray-700">{document.business_name}</span>
                ),
            },
            {
                name: '',
                field: '__actions__',
                disableSorting: true,
                style: {widthMultiplier: 0.3},
                customRender: (document: DocumentToCheck) => (
                    <DocumentActionsMenu document={document} onDeleteClick={handleDeleteClick}/>
                ),
            },
        ],
    }), [documents.length, currentSortingState, onSortingChange, onRowClick, handleDeleteClick, onPossibleDuplicateClick, isLoading]);

    const TableComponent = createTable<DocumentToCheck>(documents, tableSpec);

    return (
        <>
            {TableComponent()}
            <Popup
                open={!!documentPendingDeletion}
                onClose={() => setDocumentPendingDeletion(null)}
                onConfirm={handleDeleteConfirm}
                title="Document verwijderen"
                variant="danger"
                confirmButtonDisplayLabel="Verwijderen"
            >
                <p className="text-sm text-gray-700">
                    Weet je zeker dat je dit document wilt verwijderen?
                </p>
            </Popup>
        </>
    );
}
